import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ShoppingCart, Phone, Mail, Check, ChevronLeft, ChevronRight, MessageSquare, Info, ChevronDown, FileText, Download, Truck } from 'lucide-react';
import { categories } from '../data/products';
import { useCart } from '../components/CartContext';
import { QuoteModal } from '../components/QuoteModal';
import { SameDayBadge } from '../components/SameDayBadge';
import { generateSpecSheet } from '../utils/generateSpecSheet';
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = ''
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
  }[];
  placeholder: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-2328">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full px-4 py-3 bg-brand-lightgray/50 dark:bg-[#1a1a1a] border border-stone-200 dark:border-[#555555] rounded-none text-brand-charcoal dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all flex items-center justify-between font-medium ${value ? '' : 'text-stone-400'}`} data-id="element-2329">
        <span className="truncate" data-id="element-2330">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-2331" />
      </button>

      <AnimatePresence data-id="element-2332">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 w-full bottom-full mb-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-white/10 rounded-lg shadow-xl overflow-hidden max-h-60 overflow-y-auto" data-id="element-2333">
            {options.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
          if (!opt.disabled) {
            onChange(opt.value);
            setIsOpen(false);
          }
        }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${opt.disabled ? 'text-stone-300 dark:text-white/30 cursor-not-allowed bg-stone-50 dark:bg-stone-900/50' : value === opt.value ? 'bg-brand-orange/20 text-brand-orange' : 'text-brand-charcoal dark:text-white hover:bg-stone-50 dark:hover:bg-white/10'}`} data-id="element-2334">
                {opt.label}
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const ProductImageCarousel = ({
  images,
  alt
}: {
  images: string[];
  alt: string;
}) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
  }, [images]);
  if (!images || images.length <= 1) {
    return <img src={images[0]} alt={alt} className="absolute inset-0 w-full h-full object-cover" data-id="element-2335" />;
  }
  return <div className="absolute inset-0 w-full h-full group" data-id="element-2336">
      <img src={images[idx]} alt={alt} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300" data-id="element-2337" />
      <div className="absolute inset-0 flex items-center justify-between p-4 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2338">
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === 0 ? images.length - 1 : i - 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-2339">
          <ChevronLeft className="w-5 h-5" data-id="element-2340" />
        </button>
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === images.length - 1 ? 0 : i + 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-2341">
          <ChevronRight className="w-5 h-5" data-id="element-2342" />
        </button>
      </div>
      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2" data-id="element-2343">
        {images.map((_, i) => <button key={i} onClick={e => {
        e.stopPropagation();
        setIdx(i);
      }} className={`w-2 h-2 rounded-full transition-colors ${i === idx ? 'bg-white' : 'bg-white/50 hover:bg-white/75'}`} data-id="element-2344" />)}
      </div>
    </div>;
};
export function Product() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    addToCart
  } = useCart();
  const [quantity, setQuantity] = useState<string>('12');
  const [numLoads, setNumLoads] = useState<string>('');
  const [addedFlash, setAddedFlash] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'specs' | 'docs'>('overview');
  // Build flat list of all products for prev/next navigation
  const allProducts = useMemo(() => {
    const list: {
      slug: string;
      name: string;
      categoryName: string;
    }[] = [];
    for (const cat of categories) {
      for (const prod of cat.products) {
        list.push({
          slug: prod.slug,
          name: prod.name,
          categoryName: cat.name
        });
      }
    }
    return list;
  }, []);
  // Find product and its category
  let product = null;
  let category = null;
  for (const cat of categories) {
    const found = cat.products.find(p => p.slug === slug);
    if (found) {
      product = found;
      category = cat;
      break;
    }
  }
  const currentIndex = allProducts.findIndex(p => p.slug === slug);
  const prevProduct = currentIndex > 0 ? allProducts[currentIndex - 1] : null;
  const nextProduct = currentIndex < allProducts.length - 1 ? allProducts[currentIndex + 1] : null;
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!product || !category) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-id="element-2345">
        <h1 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-2346">
          Product not found
        </h1>
        <button onClick={() => navigate('/')} className="text-brand-orange hover:underline" data-id="element-2347">
          Return to Home
        </button>
      </div>;
  }
  const isSpecialty = category.name === 'Specialty Rock';
  const pn = product.name.toLowerCase();
  const isLight = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('50/50') || pn.includes('fill dirt') || pn.includes('play chips');
  const isLimitedTo12 = product.name === 'Builders Sand';
  const handleAddToCart = () => {
    if (quantity === 'multi' && numLoads === '3+') {
      setIsQuoteModalOpen(true);
      return;
    }
    let finalQuantity = Number(quantity);
    if (quantity === 'multi') {
      const deliveries = Number(numLoads);
      const fullLoadYards = isLight ? 15 : 12;
      finalQuantity = fullLoadYards * deliveries;
    }
    addToCart({
      category: category.name,
      product: product.name,
      quantity: finalQuantity,
      pricePerYard: product.pricePerYard
    });
    setAddedFlash(true);
    setTimeout(() => setAddedFlash(false), 2000);
  };
  // Handle multi-load custom quote redirect
  useEffect(() => {
    if (quantity === 'multi' && numLoads === '3+') {
      setIsQuoteModalOpen(true);
      setNumLoads('');
      setQuantity('12');
    }
  }, [quantity, numLoads, isLight]);
  return <motion.main initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.35,
    ease: 'easeOut'
  }} className="py-12 md:py-20 bg-[#fdfbf7] dark:bg-[#1b1b1b] min-h-[calc(100vh-64px)] transition-colors duration-300" data-id="element-2348">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2349">
        {/* Top nav: Back + Prev/Next */}
        <div className="flex items-center justify-between mb-8" data-id="element-2350">
          <Link to="/#materials" className="inline-flex items-center text-sm font-medium text-brand-muted hover:text-brand-orange transition-colors" data-id="element-2351">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2352" />
            Back to Materials
          </Link>
          <div className="flex items-center gap-2" data-id="element-2353">
            {prevProduct ? <Link to={`/shop/${prevProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-white dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={prevProduct.name} data-id="element-2354">
                <ChevronLeft className="w-4 h-4" data-id="element-2355" />
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-2356">
                  {prevProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-2357">Prev</span>
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-2358">
                <ChevronLeft className="w-4 h-4" data-id="element-2359" />
              </span>}
            {nextProduct ? <Link to={`/shop/${nextProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-white dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={nextProduct.name} data-id="element-2360">
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-2361">
                  {nextProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-2362">Next</span>
                <ChevronRight className="w-4 h-4" data-id="element-2363" />
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-2364">
                <ChevronRight className="w-4 h-4" data-id="element-2365" />
              </span>}
          </div>
        </div>

        <div className="bg-[#faf8f6] dark:bg-[#333333] rounded-2xl border border-stone-200 dark:border-[#444444] overflow-hidden shadow-sm flex flex-col md:flex-row" data-id="element-2366">
          {/* Image */}
          <div className="w-full md:w-1/2 h-64 md:h-auto relative" data-id="element-2367">
            <ProductImageCarousel images={product.images || [product.image]} alt={product.name} data-id="element-2368" />
          </div>

          {/* Content */}
          <div className="w-full md:w-1/2 p-6 sm:p-8 md:p-10 flex flex-col" data-id="element-2369">
            <div className="mb-2" data-id="element-2370">
              <span className="text-xs font-bold text-brand-muted uppercase tracking-wider" data-id="element-2371">
                {category.name}
              </span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-brand-charcoal dark:text-white mb-6" data-id="element-2372">
              {product.name}
            </h1>

            {/* Tabs */}
            <div className="flex border-b border-stone-200 dark:border-[#444] mb-6" data-id="element-2373">
              <button onClick={() => setActiveTab('overview')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'overview' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2374">
                Overview
                {activeTab === 'overview' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2375" />}
              </button>
              <button onClick={() => setActiveTab('specs')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'specs' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2376">
                Specs
                {activeTab === 'specs' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2377" />}
              </button>
              <button onClick={() => setActiveTab('docs')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'docs' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2378">
                Documents
                {activeTab === 'docs' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2379" />}
              </button>
            </div>

            <div className="mb-8 min-h-[200px]" data-id="element-2380">
              <AnimatePresence mode="wait" data-id="element-2381">
                {activeTab === 'overview' && <motion.div key="overview" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2382">
                    <p className="text-sm text-brand-charcoal/80 dark:text-white/80 mb-6 leading-relaxed" data-id="element-2383">
                      {product.description}
                    </p>

                    {product.bestFor && product.bestFor.length > 0 && <div className="mb-6" data-id="element-2384">
                        <h3 className="text-xs font-bold text-brand-charcoal dark:text-white uppercase tracking-wider mb-3" data-id="element-2385">
                          Best For
                        </h3>
                        <div className="flex flex-wrap gap-2" data-id="element-2386">
                          {product.bestFor.map((tag, idx) => <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full bg-brand-lightgray dark:bg-[#2a2a2a] text-xs font-medium text-brand-charcoal dark:text-stone-300 border border-stone-200 dark:border-[#444444]" data-id="element-2387">
                              {tag}
                            </span>)}
                        </div>
                      </div>}

                    {product.proTip && <div className="bg-brand-orange/10 border border-brand-orange/20 p-4 rounded-xl flex items-start gap-3" data-id="element-2388">
                        <div className="bg-brand-orange/20 p-1.5 rounded-full shrink-0 mt-0.5" data-id="element-2389">
                          <Check className="w-4 h-4 text-brand-orange" data-id="element-2390" />
                        </div>
                        <div data-id="element-2391">
                          <h4 className="text-sm font-bold text-brand-charcoal dark:text-white mb-1" data-id="element-2392">
                            Pro Tip
                          </h4>
                          <p className="text-sm text-brand-charcoal/80 dark:text-stone-300 leading-relaxed" data-id="element-2393">
                            {product.proTip}
                          </p>
                        </div>
                      </div>}
                  </motion.div>}

                {activeTab === 'specs' && <motion.div key="specs" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2394">
                    {product.specs ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2395">
                        {Object.entries(product.specs).map(([key, value]) => {
                    const labels: Record<string, string> = {
                      size: 'Size / Gradation',
                      weightPerYard: 'Density (Weight/Yd)',
                      compaction: 'Compaction Rating',
                      coverage: 'Est. Coverage'
                    };
                    return <div key={key} className="bg-stone-50 dark:bg-[#222222] p-4 rounded-xl border border-stone-200 dark:border-[#333333]" data-id="element-2396">
                              <p className="text-[10px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-1.5" data-id="element-2397">
                                {labels[key] || key}
                              </p>
                              <p className="text-sm font-medium text-stone-900 dark:text-stone-200" data-id="element-2398">
                                {value}
                              </p>
                            </div>;
                  })}
                      </div> : <div className="text-center py-8 text-stone-500" data-id="element-2399">
                        No detailed specifications available for this product.
                      </div>}
                  </motion.div>}

                {activeTab === 'docs' && <motion.div key="docs" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="space-y-3" data-id="element-2400">
                    {/* Auto-generated Spec Sheet — real, downloadable */}
                    <button onClick={() => generateSpecSheet({
                  product,
                  categoryName: category.name
                })} className="w-full flex items-center justify-between p-4 rounded-xl border border-brand-orange/30 bg-brand-orange/5 hover:bg-brand-orange/10 hover:border-brand-orange/50 transition-all group text-left" data-id="element-2401">
                      <div className="flex items-center gap-3 min-w-0" data-id="element-2402">
                        <div className="w-10 h-10 rounded-lg bg-brand-orange/15 flex items-center justify-center shrink-0" data-id="element-2403">
                          <FileText className="w-5 h-5 text-brand-orange" data-id="element-2404" />
                        </div>
                        <div className="min-w-0" data-id="element-2405">
                          <p className="text-sm font-bold text-stone-900 dark:text-white" data-id="element-2406">
                            Product Spec Sheet
                          </p>
                          <p className="text-xs text-stone-500 dark:text-stone-400 truncate" data-id="element-2407">
                            One-page summary · specs, coverage, pricing
                          </p>
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-orange shrink-0 ml-3" data-id="element-2408">
                        <Download className="w-4 h-4" data-id="element-2409" />
                        <span className="hidden sm:inline" data-id="element-2410">Download</span>
                      </span>
                    </button>

                    {/* Need more? Email us — honest CTA for additional docs */}
                    <div className="flex items-start gap-3 p-4 rounded-xl border border-stone-200 dark:border-[#444] bg-stone-50 dark:bg-[#2a2a2a]" data-id="element-2411">
                      <div className="w-10 h-10 rounded-lg bg-stone-100 dark:bg-[#333] flex items-center justify-center shrink-0" data-id="element-2412">
                        <Mail className="w-4 h-4 text-stone-500 dark:text-stone-400" data-id="element-2413" />
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-2414">
                        <p className="text-sm font-bold text-stone-900 dark:text-white mb-0.5" data-id="element-2415">
                          Need certified docs?
                        </p>
                        <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed" data-id="element-2416">
                          Gradation curves, MSDS, and compaction test data are
                          available on request.{' '}
                          <a href={`mailto:info@253gravel.com?subject=${encodeURIComponent(`Certified docs request: ${product.name}`)}`} className="text-brand-orange font-semibold hover:underline break-all" data-id="element-2417">
                            info@253gravel.com
                          </a>
                          .
                        </p>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <div className="mt-auto" data-id="element-2418">
              {isSpecialty ? <div className="space-y-4" data-id="element-2419">
                  <div className="p-4 bg-brand-lightgray/50 dark:bg-[#222222] rounded-xl border border-stone-200 dark:border-[#444444]" data-id="element-2420">
                    <p className="text-sm text-brand-charcoal dark:text-stone-300" data-id="element-2421">
                      Pricing and availability for specialty rock varies. Please
                      contact us directly for a custom quote.
                    </p>
                  </div>
                  <a href="tel:206-619-3742" className="w-full bg-brand-charcoal dark:bg-white text-white dark:text-brand-charcoal hover:bg-brand-charcoal/90 dark:hover:bg-stone-200 py-4 rounded-xl font-bold text-lg transition-colors flex items-center justify-center" data-id="element-2422">
                    <Phone className="w-5 h-5 mr-2" data-id="element-2423" />
                    Call for Pricing
                  </a>
                </div> : <div className="space-y-6" data-id="element-2424">
                  <div className="flex items-end justify-between border-b border-stone-100 dark:border-[#444444] pb-6" data-id="element-2425">
                    <div data-id="element-2426">
                      <p className="text-sm text-brand-muted dark:text-stone-500 font-medium mb-1" data-id="element-2427">
                        Price
                      </p>
                      {(() => {
                    const fullLoad = isLight ? 15 : 12;
                    const totalYards = quantity === 'multi' && numLoads && numLoads !== '3+' ? Number(numLoads) * fullLoad : quantity !== 'multi' ? Number(quantity) : 0;
                    const fullLoadSize = isLight ? 15 : 12;
                    const bulkPct = totalYards >= fullLoadSize * 4 ? 0.1 : totalYards >= (isLight ? fullLoadSize : fullLoadSize * 2) ? 0.05 : 0;
                    const discountedPrice = bulkPct > 0 ? Math.ceil(product.pricePerYard * (1 - bulkPct)) : product.pricePerYard;
                    return <>
                            <p className="text-3xl font-heading font-bold text-brand-charcoal dark:text-white" data-id="element-2428">
                              ${discountedPrice}{' '}
                              <span className="text-base font-normal text-brand-muted dark:text-stone-500" data-id="element-2429">
                                / yard
                              </span>
                              {bulkPct > 0 && <span className="inline-flex items-center text-xs font-bold text-stone-900 bg-amber-300 dark:bg-amber-400 px-2 py-0.5 rounded-md ml-2 align-middle uppercase tracking-wide" data-id="element-2430">
                                  {Math.round(bulkPct * 100)}% Bulk Discount
                                </span>}
                            </p>
                            {bulkPct > 0 && <p className="text-sm text-brand-muted dark:text-stone-500 mt-1" data-id="element-2431">
                                <span className="line-through" data-id="element-2432">
                                  ${product.pricePerYard}/yd
                                </span>
                                <span className="text-amber-600 dark:text-amber-400 ml-2 font-semibold" data-id="element-2433">
                                  Save $
                                  {(product.pricePerYard - discountedPrice) * totalYards}{' '}
                                  on {totalYards} yards
                                </span>
                              </p>}
                          </>;
                  })()}
                    </div>
                  </div>

                  <div className="space-y-4" data-id="element-2434">
                    <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-2435">
                      <label className="block text-sm font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2436">
                        Select Quantity
                      </label>
                      {(() => {
                    const fullLoad = isLight ? 15 : 12;
                    const tier2Yards = isLight ? fullLoad : fullLoad * 2;
                    const tier3Yards = fullLoad * 4;
                    return <span className="text-[11px] text-brand-muted dark:text-stone-400" data-id="element-2437">
                            5% at {tier2Yards} yd ·{' '}
                            <span className="font-semibold text-[#AEB9B2] dark:text-[#6FB389]" data-id="element-2438">
                              10% at {tier3Yards} yd
                            </span>
                          </span>;
                  })()}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4" data-id="element-2439">
                      <div className="w-full sm:w-1/2" data-id="element-2440">
                        <CustomSelect value={quantity} onChange={val => {
                      setQuantity(val);
                      if (val !== 'multi') setNumLoads('');
                    }} placeholder="Quantity" options={isLight ? [{
                      value: '7',
                      label: '7 Yards'
                    }, {
                      value: '8',
                      label: '8 Yards'
                    }, {
                      value: '9',
                      label: '9 Yards'
                    }, {
                      value: '10',
                      label: '10 Yards'
                    }, {
                      value: '11',
                      label: '11 Yards'
                    }, {
                      value: '12',
                      label: '12 Yards'
                    }, {
                      value: '13',
                      label: '13 Yards'
                    }, {
                      value: '14',
                      label: '14 Yards'
                    }, {
                      value: '15',
                      label: '15 Yards (Full Load)'
                    }, {
                      value: 'multi',
                      label: 'More than 1 load'
                    }] : [{
                      value: '6',
                      label: '6 Yards'
                    }, {
                      value: '7',
                      label: '7 Yards'
                    }, {
                      value: '8',
                      label: '8 Yards'
                    }, {
                      value: '9',
                      label: '9 Yards'
                    }, {
                      value: '10',
                      label: '10 Yards'
                    }, {
                      value: '11',
                      label: '11 Yards'
                    }, {
                      value: '12',
                      label: '12 Yards (Full Load)'
                    }, {
                      value: 'multi',
                      label: 'More than 1 load'
                    }]} data-id="element-2441" />

                        <AnimatePresence data-id="element-2442">
                          {quantity === 'multi' && <motion.div initial={{
                        opacity: 0
                      }} animate={{
                        opacity: 1
                      }} exit={{
                        opacity: 0
                      }} transition={{
                        duration: 0.2
                      }} className="overflow-visible mt-3" data-id="element-2443">
                              <CustomSelect value={numLoads} onChange={val => setNumLoads(val)} placeholder="How many loads?" options={[{
                          value: '2',
                          label: `2 Loads (${isLight ? '30' : '24'} yds)`
                        }, {
                          value: '3',
                          label: `3 Loads (${isLight ? '45' : '36'} yds)`
                        }, {
                          value: '4',
                          label: `4 Loads (${isLight ? '60' : '48'} yds)`
                        }, {
                          value: '3+',
                          label: '5+ Loads (Custom Quote)'
                        }]} data-id="element-2444" />
                            </motion.div>}
                        </AnimatePresence>
                      </div>

                      {addedFlash ? <motion.div initial={{
                    opacity: 0,
                    scale: 0.95
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} className="flex-1 flex items-center justify-center bg-[#76cc26]/10 dark:bg-[#76cc26]/10 text-[#76cc26] dark:text-[#76cc26] rounded-none font-bold border border-[#76cc26]/30 dark:border-[#76cc26]/30 py-3 sm:py-0" data-id="element-2445">
                          <Check className="w-5 h-5 mr-2" data-id="element-2446" />
                          Added to Truck
                        </motion.div> : <button onClick={handleAddToCart} disabled={quantity === 'multi' && !numLoads} className={`flex-1 py-3 rounded-none font-bold transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed ${quantity && quantity !== 'multi' ? 'bg-brand-button hover:bg-brand-buttonHover text-white shadow-[0_0_12px_rgba(5,137,77,0.4)]' : quantity === 'multi' && numLoads && numLoads !== '3+' ? 'bg-brand-button hover:bg-brand-buttonHover text-white shadow-[0_0_12px_rgba(5,137,77,0.4)]' : 'bg-brand-charcoal dark:bg-white/10 text-white dark:text-white hover:bg-brand-charcoal/90 dark:hover:bg-white/15 border border-transparent dark:border-white/10'}`} data-id="element-2447">
                          <Truck className="w-5 h-5 mr-2" data-id="element-2448" />
                          Add to Truck
                        </button>}
                    </div>

                    <div className="flex justify-center" data-id="element-2449">
                      <SameDayBadge variant="full" data-id="element-2450" />
                    </div>

                    <AnimatePresence data-id="element-2451">
                      {quantity === 'multi' && numLoads && numLoads !== '3+' && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="overflow-hidden" data-id="element-2452">
                            <div className="flex items-start gap-2 mt-3 p-3 bg-brand-orange/10 border border-brand-orange/20 rounded-lg" data-id="element-2453">
                              <Info className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-2454" />
                              <p className="text-xs text-brand-charcoal dark:text-stone-300" data-id="element-2455">
                                Multi-load orders require separate deliveries.
                                You will be charged a delivery fee per load.
                              </p>
                            </div>
                          </motion.div>}
                    </AnimatePresence>

                    {quantity === 'multi' && !numLoads && <div className="flex items-start gap-2 mt-3 p-3 bg-brand-orange/10 border border-brand-orange/20 rounded-lg" data-id="element-2456">
                        <Info className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-2457" />
                        <p className="text-xs text-brand-charcoal dark:text-stone-300" data-id="element-2458">
                          Please select how many loads you need.
                        </p>
                      </div>}
                  </div>

                  {quantity === 'multi' && numLoads === '3+' && <button onClick={() => setIsQuoteModalOpen(true)} className="w-full bg-brand-button hover:bg-brand-buttonHover text-white py-4 rounded-none font-bold text-lg transition-colors flex items-center justify-center" data-id="element-2459">
                      Get a Custom Quote
                    </button>}

                  {/* Custom amount / bulk note */}
                  <p className="text-xs text-brand-charcoal/70 dark:text-white/70 text-center" data-id="element-2460">
                    Need a custom amount?{' '}
                    <a href="tel:206-619-3742" className="text-brand-orange hover:underline" data-id="element-2461">
                      Call us
                    </a>{' '}
                    or{' '}
                    <Link to="/contact" className="text-brand-orange hover:underline" data-id="element-2462">
                      contact us
                    </Link>
                    .
                  </p>

                  {/* Helpful resource links */}
                  <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pt-2" data-id="element-2463">
                    <Link to={`/compare?add=${encodeURIComponent(product.slug)}`} className="text-xs text-brand-charcoal/70 dark:text-white/60 hover:text-brand-orange dark:hover:text-brand-orange transition-colors underline underline-offset-2" data-id="element-2464">
                      Compare Products
                    </Link>
                    <span className="text-xs text-brand-charcoal/30 dark:text-white/20" data-id="element-2465">
                      ·
                    </span>
                    <Link to="/compare" className="text-xs text-brand-charcoal/70 dark:text-white/60 hover:text-brand-orange dark:hover:text-brand-orange transition-colors underline underline-offset-2" data-id="element-2466">
                      Compare Materials
                    </Link>
                    <span className="text-xs text-brand-charcoal/30 dark:text-white/20" data-id="element-2467">
                      ·
                    </span>
                    <Link to="/help" className="text-xs text-brand-charcoal/70 dark:text-white/60 hover:text-brand-orange dark:hover:text-brand-orange transition-colors underline underline-offset-2" data-id="element-2468">
                      Quiz & Calculator
                    </Link>
                    <span className="text-xs text-brand-charcoal/30 dark:text-white/20" data-id="element-2469">
                      ·
                    </span>
                    <Link to="/diy-help" className="text-xs text-brand-charcoal/70 dark:text-white/60 hover:text-brand-orange dark:hover:text-brand-orange transition-colors underline underline-offset-2" data-id="element-2470">
                      DIY Help
                    </Link>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-2471" />
    </motion.main>;
}